import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/lib/auth/auth-context"
import { queryKeys } from "@/lib/api/query-keys"
import type { TeamType } from "@/lib/api/types"
import { makeDealershipRow, type TeamRow } from "@/components/onboarding/team-row-card"

// Onboarding step set. Two flows visit different subsets:
//
//   Dealership / Bodyshop:
//     personal → company → location → invite → extra
//
//   DealershipGroup:
//     personal → company → location → extra (Dealerships) → bodyshops (Body shops)
//
// Group skips `invite` (per-team invites happen on the dealerships/shops
// they're declaring, not on the group itself) and gets a separate
// `bodyshops` step that comes AFTER `extra`. Non-group flows skip
// `bodyshops` (their step 5 is `extra` itself: a body-shop connect, the
// fees form, etc).
export type OnboardingStep =
  | "personal"
  | "company"
  | "location"
  | "invite"
  | "extra"
  | "bodyshops"

export const ONBOARDING_STEPS: OnboardingStep[] = [
  "personal",
  "company",
  "location",
  "invite",
  "extra",
  "bodyshops",
]

export interface CompanySnapshot {
  name: string
  city: string
  state: string
}

export interface ProfileDraft {
  firstName: string
  lastName: string
}

// Held in context so step 2a (Identity) and step 2b (Location) can both
// read/write without losing data on back-nav, and so the team is created in
// a single API call at the end of 2b — no orphan team records mid-flow.
export interface CompanyDraft {
  type: "" | "Dealership" | "Bodyshop" | "DealershipGroup"
  name: string
  street: string
  city: string
  state: string
  zipCode: string
  country: string
  phoneNumber: string
}

export const EMPTY_COMPANY_DRAFT: CompanyDraft = {
  type: "",
  name: "",
  street: "",
  city: "",
  state: "",
  zipCode: "",
  country: "United States",
  phoneNumber: "",
}

// Slim record of a dealership the group user just created in the
// "Dealerships" step. Surfaced to the next step ("Body shops") so each shop
// row can pick which dealerships it serves.
export interface CreatedDealership {
  teamId: number
  name: string
  city: string
  state: string
}

interface OnboardingContextValue {
  step: OnboardingStep
  stepIndex: number
  totalSteps: number
  direction: 1 | -1
  selectedType: TeamType | null
  setSelectedType: (t: TeamType | null) => void
  // Role the user picks for themselves once they choose org type. Passed
  // along to teamsApi.create as `ownerRole` so the BE assigns it on the
  // teamUsers record. Lives in context (not local form state) because the
  // panel previews it and step 4's confirmation can show it back.
  selectedRole: string | null
  setSelectedRole: (r: string | null) => void
  // Persistent across step 2a + 2b (and survives back-nav). Either step can
  // patch any subset; the team-creation API call at the end of 2b reads the
  // whole draft.
  companyDraft: CompanyDraft
  setCompanyDraft: (patch: Partial<CompanyDraft>) => void
  createdTeamId: number | null
  setCreatedTeamId: (id: number) => void
  // Group flow only: dealerships created in step `extra`, consumed by step
  // `bodyshops` so each shop's "Routes to" chip group can list them and
  // submit can link the right shop to the right dealership team.
  createdDealerships: CreatedDealership[]
  setCreatedDealerships: (d: CreatedDealership[]) => void
  // Group flow only: the row state for step `extra` (Dealerships) lives in
  // the provider so it survives back-nav from `bodyshops`. AnimatePresence
  // mode="wait" unmounts each step on transition, so step-local useState
  // would reset every time the user steps back to revisit the list.
  dealershipRows: TeamRow[]
  setDealershipRows: (rows: TeamRow[] | ((rs: TeamRow[]) => TeamRow[])) => void
  profileDraft: ProfileDraft
  setProfileDraft: (p: ProfileDraft) => void
  companySnapshot: CompanySnapshot | null
  setCompanySnapshot: (s: CompanySnapshot) => void
  invitedCount: number
  setInvitedCount: (n: number) => void
  goNext: () => void
  goBack: () => void
  finish: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { refreshUser } = useAuth()

  const [stepIndex, setStepIndex] = useState(0)
  const [direction, setDirection] = useState<1 | -1>(1)
  const [selectedType, setSelectedType] = useState<TeamType | null>(null)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [companyDraft, setCompanyDraftState] = useState<CompanyDraft>(EMPTY_COMPANY_DRAFT)
  const [createdTeamId, setCreatedTeamId] = useState<number | null>(null)
  const [createdDealerships, setCreatedDealerships] = useState<CreatedDealership[]>([])
  const [dealershipRows, setDealershipRows] = useState<TeamRow[]>(() => [makeDealershipRow()])
  const [companySnapshot, setCompanySnapshot] = useState<CompanySnapshot | null>(null)
  const [profileDraft, setProfileDraft] = useState<ProfileDraft>({ firstName: "", lastName: "" })
  const [invitedCount, setInvitedCount] = useState(0)

  const setCompanyDraft = useCallback((patch: Partial<CompanyDraft>) => {
    setCompanyDraftState((d) => ({ ...d, ...patch }))
  }, [])

  // Group skips `invite`; non-group skips `bodyshops`. One extra hop in
  // nav is enough because no two consecutive steps are skipped together.
  const isGroup = selectedType === "DealershipGroup"
  const isHidden = useCallback(
    (s: OnboardingStep) =>
      (isGroup && s === "invite") || (!isGroup && s === "bodyshops"),
    [isGroup],
  )

  const goNext = useCallback(() => {
    setDirection(1)
    setStepIndex((i) => {
      let next = Math.min(ONBOARDING_STEPS.length - 1, i + 1)
      if (isHidden(ONBOARDING_STEPS[next])) {
        next = Math.min(ONBOARDING_STEPS.length - 1, next + 1)
      }
      return next
    })
  }, [isHidden])

  const goBack = useCallback(() => {
    setDirection(-1)
    setStepIndex((i) => {
      let prev = Math.max(0, i - 1)
      if (isHidden(ONBOARDING_STEPS[prev])) {
        prev = Math.max(0, prev - 1)
      }
      return prev
    })
  }, [isHidden])

  const finish = useCallback(async () => {
    await Promise.all([
      refreshUser(),
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all }),
    ])
    navigate("/dashboard")
  }, [navigate, queryClient, refreshUser])

  return (
    <OnboardingContext.Provider
      value={{
        step: ONBOARDING_STEPS[stepIndex],
        stepIndex,
        totalSteps: ONBOARDING_STEPS.length,
        direction,
        selectedType,
        setSelectedType,
        selectedRole,
        setSelectedRole,
        companyDraft,
        setCompanyDraft,
        createdTeamId,
        setCreatedTeamId,
        createdDealerships,
        setCreatedDealerships,
        dealershipRows,
        setDealershipRows,
        profileDraft,
        setProfileDraft,
        companySnapshot,
        setCompanySnapshot,
        invitedCount,
        setInvitedCount,
        goNext,
        goBack,
        finish,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider")
  return ctx
}
